// eslint-disable-next-line object-curly-newline
import { isAfter, parseISO, subDays, subHours } from 'date-fns';
import _ from 'lodash';
import { validate as uuidValidate } from 'uuid';
import {
  DocumentData, Query, QuerySnapshot, getCountFromServer
} from 'firebase/firestore';
import { functionsApi } from '../config/deliveryApi';

import {
  callCollection, doc, getDoc, getDocs, limit,
  orderBy, query, startAfter, startAt, endAt, subsidiaryCollection, updateDoc, where,
  callRejectionsCollection
} from '../config/firebase';
import api from '../config/functionsApi';
import StatusCall, { convertStatusToBrazilian } from '../models/enums/statusCall';
import PaymentType from '../models/enums/paymentType';
import Call, {
  CallCounter, CallMonthReport, CallRemittanceReport, CallReportCounter, HistoryBy
} from '../models/interfaces/call';
import { CallSql } from '../models/interfaces/sql/callSql';
import DeviceType, { DeviceTypeText } from '../models/enums/deviceType';
import Dictionary from '../models/interfaces/dictionary';
import pushNotificationService from './pushNotificationService';
import subsidiaryService from './subsidiaryService';
import apiCallMicroservice, { BoazRemoteCallService } from '../config/callMicroserviceApi';
import SupabaseService from './supabase';

const supabaseService = new SupabaseService();
const countDestinations = async (subsidiaryId: string | undefined | null, fullPeriod: boolean) => {
  const maxDate = new Date();
  const minDate = fullPeriod ? new Date(maxDate.getFullYear(), 0, 1, 0, 0, 0) : new Date(maxDate.getFullYear(), maxDate.getMonth(), 1, 0, 0, 0);

  let q = query(callCollection, where('createdAt', '>=', minDate), where('complete', '==', true), where('status', '==', 'complete'));
  if (subsidiaryId) {
    q = query(q, where('subsidiary', '==', subsidiaryId));
  }
  const total = await getCountFromServer(q);
  return total?.data().count ?? 0;
};

const countDestinationsWithMemoize = _.memoize(countDestinations, (...args) => _.values(args).join('_'));

export const statusAllowedToCompleteCall = ['ongoing', 'partialComplete'];

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const anonymizeDriverId = (driverId: string | null | undefined) => {
  if (!driverId) return 'Não atribuído';
  let hash = 0;
  for (let i = 0; i < driverId.length; i += 1) {
    hash = (hash * 31 + driverId.charCodeAt(i)) % 2147483647;
  }
  return `ENT-${Math.abs(hash)
    .toString(16)
    .toUpperCase()
    .padStart(8, '0')
    .slice(-8)}`;
};

const platformLabel = (item: CallSql) => {
  const channel = item.doc?.salesChannel?.name || item.saleschannelname;
  if (channel) {
    const displayId = item.doc?.salesChannel?.displayId;
    return displayId ? `${channel} (#${displayId})` : channel;
  }
  const device = Number(item.device) as DeviceType;
  return DeviceTypeText[device] || 'App/Web';
};

const mapCallRemittance = (item: CallSql): CallRemittanceReport => {
  const destinations = item.doc?.destinations ?? [];
  const hasBack = Boolean(item.hasback ?? item.doc?.hasBack);
  let contractorValue = Number(item.paymentvalue) || Number(item.doc?.payment?.value) || 0;
  const returnValue = Number(item.paymentreturnvalue) || Number(item.doc?.payment?.returnValue) || 0;
  let jumaValue = 0;
  let feesPercent = Number(item.subsidiaryfeeperc) || 0;
  let distanceMeters = 0;

  if (destinations.length) {
    contractorValue = _.sumBy(destinations, (destination) => Number(destination.tariff?.value) || Number(destination.cost) || 0);
    jumaValue = _.sumBy(destinations, (destination) => Number(destination.tariff?.subsidiaryFeeValue) || 0);
    const last = destinations[destinations.length - 1];
    if (hasBack && last?.tariff) {
      contractorValue += Number(last.tariff.valueBack) || returnValue || 0;
      jumaValue += Number(last.tariff.subsidiaryFeeValueBack) || 0;
    }
    distanceMeters = _.sumBy(destinations, (destination) => Number(destination.distance) || Number(destination.tariff?.distanceValue) || 0);
    const percent = last?.tariff?.subsidiaryFeePerc ?? destinations[0]?.tariff?.subsidiaryFeePerc;
    if (percent !== undefined && percent !== null && percent !== '') {
      feesPercent = Number(percent) || feesPercent;
    }
  } else if (hasBack) {
    contractorValue += returnValue;
  }

  if (!jumaValue && feesPercent) {
    jumaValue = contractorValue * (feesPercent / 100);
  }

  contractorValue = roundMoney(contractorValue);
  jumaValue = roundMoney(jumaValue);
  const driverValue = roundMoney(Math.max(contractorValue - jumaValue, 0));

  const feesParts: string[] = [];
  if (feesPercent) feesParts.push(`Comissão ${feesPercent}%`);
  if (jumaValue) {
    feesParts.push(jumaValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
  }
  const feesDescription = feesParts.length ? feesParts.join(' · ') : 'Sem taxa incidente';

  const orderRef = item.doc?.salesChannel?.displayId || item.doc?.salesChannel?.orderId;
  const deliveryId = orderRef ? `${item.id ?? ''} · ${orderRef}` : (item.id ?? '');

  return {
    id: item.id ?? '',
    deliveryId,
    createdAt: item.createdat ? new Date(item.createdat) : new Date(),
    platform: platformLabel(item),
    contractorValue,
    jumaValue,
    driverValue,
    feesDescription,
    feesAmount: jumaValue,
    feesPercent,
    driverAnonymousId: anonymizeDriverId(item.driverid || item.doc?.driver?.id),
    distanceKm: roundMoney(distanceMeters / 1000)
  };
};

export interface CallAdjustmentListItem {
  id: string;
  createdAt: Date;
  clientName: string;
  driverName: string;
  status: string;
  statusHtml: { color: string; description: string };
  destinations: string[];
  canAdjust: boolean;
}

export interface DestinationAdjustmentPatch {
  comments: string | null;
  complement: string;
  updateLocation: boolean;
  address?: string;
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  placeId?: string | null;
  latitude?: number;
  longitude?: number;
  hasBack?: boolean;
  value?: number;
  valueBack?: number;
  subsidiaryFeeValue?: number;
  subsidiaryFeeValueBack?: number;
  distance?: number;
  distanceInfo?: string | null;
  duration?: number | null;
  durationInfo?: string | null;
}

export const getAdjustmentDaysAllowed = (isAdmin: boolean) => (isAdmin ? 15 : 7);

export const isCallWithinAdjustmentWindow = (createdAt: Date, daysAllowed: number) => {
  if (Number.isNaN(createdAt.getTime())) return false;
  return isAfter(createdAt, subDays(new Date(), daysAllowed));
};

const toCallDate = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const parseCallDoc = (docValue: CallSql['doc'] | string | null | undefined) => {
  if (!docValue) return null;
  if (typeof docValue === 'string') {
    try {
      return JSON.parse(docValue) as CallSql['doc'];
    } catch {
      return null;
    }
  }
  return docValue;
};

export default {
  async getAll(subsidiaryId: string | undefined | null, limitMax: number | null) {
    let q = query(callCollection, orderBy('createdAt', 'desc'));
    if (subsidiaryId) {
      q = query(q, where('subsidiary', '==', subsidiaryId));
    }

    if (limitMax) {
      q = query(q, limit(limitMax));
    }

    return getDocs(q).then(async (querySnapshot) => {
      const result = querySnapshot.docs.map((snap) => {
        const model = snap.data() as Call;

        return {
          ...model,
          createdAt: snap.data().createdAt.toDate(),
          statusHtml: convertStatusToBrazilian(model.status),
          id: snap.id
        };
      });

      return result;
    });
  },

  getAllRealTimeStream(subsidiaryId: string | null | undefined, status: string | undefined, limitMax: number = 50) {
    const minDate = subHours(new Date(), 8);
    let q = query(callCollection, where('createdAt', '>=', minDate), orderBy('createdAt', 'desc'));
    if (subsidiaryId) {
      q = query(q, where('subsidiary', '==', subsidiaryId));
    }
    if (status) {
      q = query(q, where('status', '==', status));
    }
    return query(q, limit(limitMax));
  },

  async getById(id: string) {
    const docRef = doc(callCollection, id);
    const result = await getDoc(docRef);
    if (!result.exists) throw new Error('Call not found');

    return result.data();
  },

  async getReport(id: string | null | undefined, startDate: Date, endDate: Date, clientId: string | null, driverId: string | null, paymentType: number | null) {
    let subsidiaryId = id;
    // Só resolve UUID (RDS) → id Firebase quando necessário; id Firebase já vai direto à query
    if (subsidiaryId && uuidValidate(subsidiaryId)) {
      const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
      const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
      if (!snap.empty) {
        subsidiaryId = snap.docs[0].id;
      }
    }

    let q = query(
      callCollection,
      where('subsidiaryTemp.id', '==', subsidiaryId), // TODO: Change to "subsidiary.id"
      where('complete', '==', true),
      where('status', '==', 'complete')
    );
    if (clientId) {
      q = query(q, where('origin.clientId', '==', clientId));
    }
    if (driverId) {
      q = query(q, where('driver.id', '==', driverId));
    }
    if (paymentType != null) {
      q = query(q, where('payment.type', '==', Number(paymentType)));
    }

    q = query(
      q,
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      orderBy('createdAt')
    );

    try {
      const querySnapshot = await getDocs(q);
      // Mapeia só os campos usados por revenue/billing (menos alocação em memória)
      return querySnapshot.docs.map((snap: any) => {
        const data = snap.data();
        return {
          id: snap.id,
          origin: data.origin
            ? { clientId: data.origin.clientId, responsible: data.origin.responsible }
            : null,
          driver: data.driver
            ? {
              id: data.driver.id,
              name: data.driver.name,
              photo: data.driver.photo
            }
            : null,
          payment: data.payment
            ? { type: data.payment.type, value: data.payment.value }
            : null,
          destinations: data.destinations ?? [],
          hasBack: data.hasBack ?? false,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt
        };
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
      return [];
    }
  },

  async getSumCallsMonth(subId: string | null | undefined): Promise<CallReportCounter[]> {
    const data: CallReportCounter[] = [];
    const rows = await this.getCallBySubsidiary(subId);

    rows.forEach((item: any) => {
      data.push({
        id: item.id,
        value: item.value
      });
    });

    // GEt from Microservice calls

    // let q = query(callCollection, where('subsidiaryTemp.id', '==', subsidiaryId), // TODO: Change to "subsidiary.id"
    //   where('complete', '==', true),
    //   where('status', '==', 'complete'),
    //   where('createdAt', '>=', startDate),
    //   where('createdAt', '<=', endDate));
    // if (clientId) {
    //   q = query(q, where('origin.clientId', '==', clientId));
    // }
    // if (driverId) {
    //   q = query(q, where('driver.id', '==', driverId));
    // }
    // const result = await getDocs(q);

    // result.forEach((snap) => {
    //   const item = snap.data();
    //   data.push({
    //     id: snap.id,
    //     value: item.payment.value
    //   });
    // });

    return data;
  },

  async getAllReport(id: string | null | undefined, startDate: Date, endDate: Date, status: Dictionary[]) {
    let subsidiaryId = id;

    let q: Query;

    if (status.length === 1) {
      q = query(callCollection, where('status', '==', status[0].key),
        orderBy('createdAt'),
        startAt(startDate),
        endAt(endDate));
    } else {
      q = query(callCollection, where('status', 'in', status.map((x) => x.key)),
        orderBy('createdAt'),
        startAt(startDate),
        endAt(endDate));
    }

    if (subsidiaryId) {
      if (uuidValidate(subsidiaryId)) {
        const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
        const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
        if (!snap.empty) {
          subsidiaryId = snap.docs[0].id;
        }
      }
      q = query(q, where('subsidiaryTemp.id', '==', subsidiaryId)); // TODO: Change to "subsidiary.id"
    }

    return getDocs(q).then(async (querySnapshot) => {
      const result = querySnapshot.docs.map((snap) => {
        const model = snap.data() as Call;

        return {
          ...model,
          id: snap.id,
          statusHtml: convertStatusToBrazilian(model.status),
          createdAt: snap.data().createdAt.toDate()
        };
      });

      return result;
    });
  },

  async count(subsidiaryId: string | undefined | null, fullPeriod = false) {
    return countDestinationsWithMemoize(subsidiaryId, fullPeriod);
  },

  async getCountTotalCanceled(days: number) {
    const maxDate = new Date();
    const minDate = subDays(new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 0, 0, 0), days - 1);
    const c = query(callCollection, where('createdAt', '>=', minDate), where('status', '==', 'canceled'));
    const total = await getCountFromServer(c);
    return total?.data().count;
  },

  async deliveriesDaysSupabse(days: number, subsidiaryId: string | undefined | null, timezone: string | null) {
    const data = await supabaseService.getCallsDaysApi(days, subsidiaryId, timezone);
    return data;
  },

  async getCallsByMonth(subsidiaryId: string | undefined | null, timezone: string | null) {
    const today = new Date();
    const data = await supabaseService.getCallsByMonth(subsidiaryId, timezone, today.getFullYear());
    return data;
  },

  // async deliveriesByMonth(subsidiaryId: string | undefined | null, timezone: string | null) {
  //   const date = new Date();
  //   const auth = await BoazRemoteCallService.getNewToken();
  //   const config = {
  //     headers: {
  //       'Content-Type': 'application/json',
  //       Authorization: `Bearer ${auth?.token}`
  //     },
  //     params: {
  //       subsidiaryId: subsidiaryId || '',
  //       environment: process.env.REACT_APP_ADMIN_PROJECT_ID,
  //       monthini: 1,
  //       monthfin: date.getMonth() + 1,
  //       year: date.getFullYear(),
  //       timezone
  //     }
  //   };
  //   const response = await apiCallMicroservice.get('calls/deliveriesbymonth', config);
  //   return response.data;
  // },

  async countCallsByClients(subsidiaryId: string | undefined | null, timezone: string | null) {
    const auth = await BoazRemoteCallService.getNewToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.token}`
      },
      params: {
        subsidiaryId: subsidiaryId || '',
        environment: process.env.REACT_APP_ADMIN_PROJECT_ID,
        timezone
      }
    };
    const response = await apiCallMicroservice.get('calls/countcallsbyclients', config);
    return response.data;
  },

  async countCallsByDrivers(subsidiaryId: string | undefined | null, timezone: string | null) {
    const auth = await BoazRemoteCallService.getNewToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.token}`
      },
      params: {
        subsidiaryId: subsidiaryId || '',
        environment: process.env.REACT_APP_ADMIN_PROJECT_ID,
        timezone
      }
    };
    const response = await apiCallMicroservice.get('calls/countcallsbydrivers', config);
    return response.data;
  },

  async sumcallsbymonth(subsidiaryId: string | undefined | null, timezone: string | null) {
    const auth = await BoazRemoteCallService.getNewToken();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.token}`
      },
      params: {
        subsidiaryId: subsidiaryId || '',
        environment: process.env.REACT_APP_ADMIN_PROJECT_ID,
        timezone
      }
    };
    const response = await apiCallMicroservice.get('calls/sumcallsbymonth', config);
    return response.data;
  },

  async countCallsbymonth(subsidiaryId: string | undefined | null, timezone: string | null, year: number) {
    const data = await supabaseService.getCallsByMonth(subsidiaryId, timezone, year);
    return data;
  },

  prepareListFromFirestore(result: QuerySnapshot<DocumentData>) {
    const data: CallCounter[] = [];
    result.forEach((snap) => {
      const item = snap.data();
      // TODO: Remove after Aldenor fix this
      const pock = item.availableDrivers ?? {};
      data.push({
        id: snap.id,
        createdAt: item.createdAt.toDate(),
        status: item.status,
        categoryDriver: item.categoryDriver,
        destinations: item.destinations,
        device: item.device,
        availableDrivers: Object.values(pock),
        subsidiaryId: item.subsidiaryId,
        subsidiaryName: item.subsidiaryName
      });
    });
    return data;
  },

  prepareListFromJSONList(result: any[]) {
    const data: CallCounter[] = [];
    result?.forEach((item) => {
      // TODO: Remove after Aldenor fix this
      const pock = item.availableDrivers ?? {};
      data.push({
        id: item.id,
        createdAt: new Date(item.createdat),
        status: item.status,
        categoryDriver: Number(item.drivercategory),
        destinations: Array.from(Array(item.deliveries).keys()),
        device: Number(item.device),
        availableDrivers: Object.values(pock),
        subsidiaryId: item.subsidiaryid,
        subsidiaryName: item.subsidiaryname
      });
    });
    return data;
  },

  async countLastDays(days: number, subsidiaryId: string | undefined | null, timezone: string | null) {
    // const maxDate = new Date();
    // const minDate = subDays(new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 0, 0, 0), days - 1);

    // const auth = await BoazRemoteCallService.getNewToken();
    // const config = {
    //   headers: {
    //     'Content-Type': 'application/json',
    //     Authorization: `Bearer ${auth?.token}`
    //   },
    //   params: {
    //     subsidiaryId: subsidiaryId || '',
    //     environment: process.env.REACT_APP_ADMIN_PROJECT_ID,
    //     dataini: `${minDate.getFullYear()}-${minDate.getMonth() + 1}-${minDate.getDate()}`,
    //     datefin: `${maxDate.getFullYear()}-${maxDate.getMonth() + 1}-${maxDate.getDate()}`,
    //     timezone
    //   }
    // };

    // Use index in firebase
    // get canceled from firebase
    // let q = query(callCollection, where('createdAt', '>=', minDate), where('status', '==', 'canceled'));
    // if (subsidiaryId) {
    //   q = query(q, where('subsidiary', '==', subsidiaryId));
    // } else {
    //   q = query(q, where('subsidiary', '==', 0));
    // }

    // const result = await getDocs(q);
    // const data = this.prepareListFromFirestore(result);

    // firebase
    const calls = await this.deliveriesDaysSupabse(days, subsidiaryId, timezone);
    // console.log(calls);
    const data = this.prepareListFromJSONList(calls);
    // const response = await apiCallMicroservice.get('calls/bybetweendate', config);
    // eslint-disable-next-line no-console
    // const resultFromApi = [];
    // if (response?.status === 200) {
    //   const dataResp = response?.data?.calls?.map((row: any) => ({
    //     ...row,
    //     createdAt: row.createdAt ? new Date(row.createdAt) : new Date()
    //   }));
    //   // resultFromApi.push(...dataResp);
    //   const allCalls = data.concat(dataResp);
    //   return _.sortBy(allCalls, (x) => x.createdAt);
    // }

    return _.sortBy(data, (x) => x.createdAt);
  },

  async fetchNextData(key: any, id: string | undefined | null, status: string | null | undefined, limitMax: number = 50) {
    let subsidiaryId = id;
    const maxDate = new Date();
    const minDate = subHours(maxDate, 12);
    let q = query(callCollection, where('createdAt', '>=', minDate), where('createdAt', '<=', maxDate), orderBy('createdAt', 'desc'));
    if (subsidiaryId) {
      if (uuidValidate(subsidiaryId)) {
        const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
        const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
        if (!snap.empty) {
          subsidiaryId = snap.docs[0].id;
        }
      }
      q = query(q, where('subsidiary', '==', subsidiaryId));
    }

    if (status) {
      q = query(q, where('status', '==', status));
    }
    q = query(q, startAfter(key), limit(limitMax));

    const result = await getDocs(q);

    const data: Call[] = [];
    let lastKey;
    result.forEach((snap) => {
      const item = snap.data();
      data.push({ ...item as Call, id: snap.id, createdAt: item.createdAt.toDate() });
      lastKey = snap.data().createdAt;
    });

    return { data, lastKey };
  },

  async getWithFilters(id: string | undefined | null, status: string | null | undefined) {
    let subsidiaryId = id;
    const maxDate = new Date();
    const minDate = subHours(maxDate, 12);
    let q = query(callCollection, where('createdAt', '>=', minDate), where('createdAt', '<=', maxDate), orderBy('createdAt', 'desc'));
    if (subsidiaryId) {
      if (uuidValidate(subsidiaryId)) {
        const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
        const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
        if (!snap.empty) {
          subsidiaryId = snap.docs[0].id;
        }
      }
      q = query(q, where('subsidiary', '==', subsidiaryId));
    }

    if (status) {
      q = query(q, where('status', '==', status));
    }

    q = query(q, limit(50));

    return getDocs(q).then(async (querySnapshot) => {
      const result = querySnapshot.docs.map((snap) => {
        const model = snap.data() as Call;

        return {
          ...model,
          createdAt: snap.data().createdAt.toDate(),
          statusHtml: convertStatusToBrazilian(model.status),
          id: snap.id
        };
      });

      return result;
    });
  },

  async cancelCall(id: string, owner: HistoryBy) {
    const name = owner?.name ?? 'Administrador';
    await api.patch(`calls/${id}/cancel`, { justification: `Cancelada por ${name} ` })
      .catch((e) => {
        throw new Error(e.response?.data?.message ?? 'Ops, aconteceu algo inesperado, por favor tente novamente');
      });
  },

  async cancelCallByAdmin(id: string, owner: HistoryBy, daysAllowed: number) {
    const model = await this.getById(id);
    if (!model) throw new Error('Call not found');

    const currentStatus = model.status;
    const canCancel = currentStatus !== 'canceled';

    if (!canCancel) {
      throw new Error('Operation not allowed');
    }

    const date = model.createdAt.toDate();
    if (!isAfter(date, subDays(new Date(), daysAllowed))) {
      throw new Error('Operation not allowed');
    }
    await this.cancelCall(id, owner)
      .then(async (res) => {
        console.log('res', res);
        if (model.driver) {
          const title = 'Chamada CANCELADA';
          const text = `Chamada ${model.origin.responsible} CANCELADA pelo administrador, dúvida entre em contato com o suporte da sua cidade.`;
          await pushNotificationService.sendNotificationToDriver(id, title, text, model.driver.id, model.driver.name).catch(() => {
            throw new Error('Notificacão não foi enviada para o condutor');
          });
        }
        return null;
      });
    return null;
  },

  async changeStatus(id: string, owner: HistoryBy, newStatus: string, daysAllowed: number) {
    const model = await this.getById(id);
    if (!model) throw new Error('Call not found');

    const currentStatus = model.status;

    if (newStatus === 'complete' && !statusAllowedToCompleteCall.includes(currentStatus)) {
      throw new Error('Operation not allowed');
    }

    const cancelCompleteCall = newStatus === 'canceled' && currentStatus === 'complete';

    if (cancelCompleteCall) {
      const date = model.createdAt.toDate();
      if (!isAfter(date, subDays(new Date(), daysAllowed))) {
        throw new Error('Operation not allowed');
      }
      this.cancelCall(id, owner)
        .then(async (res) => {
          console.log('res', res);
          const title = 'Chamada CANCELADA';
          const text = `Chamada ${model.origin.responsible} CANCELADA pelo administrador, dúvida entre em contato com o suporte da sua cidade.`;
          await pushNotificationService.sendNotificationToDriver(id, title, text, model.driver.id, model.driver.name).catch(() => {
            throw new Error('Notificacão não foi enviada para o condutor');
          });
          return null;
        });
      return null;
    }

    const modelRef = doc(callCollection, id);

    const newStatusHistory = model.statusHistory;
    newStatusHistory.push({
      by: owner,
      date: new Date(), // getDateNow() ocorre erro porque o firebase não suporta esse método em uma array,
      status: newStatus
    });

    const dataToUpdate = {
      complete: true,
      status: newStatus,
      statusHistory: newStatusHistory,
      canceledAt: cancelCompleteCall ? parseISO(new Date().toISOString()) : null,
      remoteCanceledAt: null
    };

    return updateDoc(modelRef, dataToUpdate).then(async () => {
      if (model.driver) {
        let header = '?';
        let body = '?';
        if (newStatus === 'complete') {
          header = 'Entrega FINALIZADA manualmente';
          body = `Sua entrega do cliente ${model.origin.responsible} foi FINALIZADA pelo administrativo, qualquer dúvida pode entrar em contato conosco.`;
          const jsonBody = { callId: id };
          functionsApi.post('calls/sync', jsonBody);
        } else if (newStatus === 'canceled') {
          header = 'Entrega CANCELADA manualmente';
          body = `Sua entrega do cliente ${model.origin.responsible} foi CANCELADA pelo administrativo, qualquer dúvida pode entrar em contato conosco.`;
        }

        await pushNotificationService.sendNotificationToDriver(id, header, body, model.driver.id, model.driver.name).catch(() => {
          throw new Error('Notificacão não foi enviada para o condutor');
        });
      }
    });
  },

  async changeDriverAsync(id: string, driverId: string) {
    await api.patch(`calls/${id}/driver`, { driverId }).catch((e) => {
      throw new Error(e.response?.data?.message ?? 'Ops, aconteceu algo inesperado, por favor tente novamente');
    });
  },

  getDestinationsCount(calls: any[]): number {
    const destinationsTotal = calls!.length ? calls.map((row) => row.destinations.length).reduce((prev, next) => prev + next) : 0;
    return destinationsTotal;
  },

  getDestinationsValueSum(calls: any[]): any {
    const totals = {
      destinationsSumValue: 0,
      destinationsSumFee: 0
    };
    calls.forEach((x) => {
      const tariffValue = _.sumBy(x.destinations, 'tariff.value');
      const subsidiaryFeeValue = _.sumBy(x.destinations, 'tariff.subsidiaryFeeValue') ?? 0;
      const lastSubsidiaryFeeValueBack = x.hasBack ? x.destinations[x.destinations.length - 1].tariff.subsidiaryFeeValueBack ?? 0 : 0;
      const lastValueBack = x.hasBack ? x.destinations[x.destinations.length - 1].tariff.valueBack ?? 0 : 0;
      totals.destinationsSumValue += (tariffValue + lastValueBack); // Get value back from last destination
      totals.destinationsSumFee += (subsidiaryFeeValue + lastSubsidiaryFeeValueBack);
    });
    return totals;
  },

  async countRefuses(id: string) {
    const c = query(callRejectionsCollection, where('callId', '==', id));
    const total = await getCountFromServer(c);
    return total?.data().count;
  },

  async getSubsidiaries() {
    const q = query(subsidiaryCollection, where('disabledAt', '==', null), orderBy('description'));
    return getDocs(q).then(async (querySnapshot) => {
      const result: any[] = [];
      querySnapshot.forEach((snapshot: any) => {
        const model = snapshot.data();
        result.push({ id: snapshot.id, description: model.description });
      });
      return result;
    });
  },

  async countDestinations(subsidiary: string, month: number, year: number) {
    const start = new Date(year, month - 1, 1, 0, 0);
    const end = new Date(year, month, 1, 0, 0);
    const c = query(callCollection,
      where('subsidiary', '==', subsidiary),
      where('status', '==', StatusCall.Complete),
      orderBy('createdAt'),
      startAt(start),
      endAt(end));
    const total = await getCountFromServer(c);
    return total?.data().count;
  },

  async propayConvert(id: string): Promise<any> {
    return api.patch(`calls/${id}/prepayconvert`).then((response) => {
      if (response?.status !== 200 || !response.data?.transact?.success) throw new Error('Serviço indisponível');
      return response.data;
    }).catch((e) => {
      throw new Error(e.response?.data?.message ?? 'Serviço inoperante no momento, por favor tente novamente');
    });
  },

  async getCallBySubsidiary(subsidiaryId: string | undefined | null, startDate: Date | null = null, endDate: Date | null = null): Promise<any[]> {
    try {
      const response = await api.get('calls/get-supabase-calls', { params: { subsidiaryId, startDate, endDate } });
      const payload = response?.data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      return [];
    } catch (error) {
      throw new Error(`Erro ao buscar chamadas no Supabase: ${error instanceof Error ? error.message : String(error)}`);
    }
  },

  async getDetailedByMonth(
    subsidiaryId: string,
    subsidiaryName: string | null,
    startDate: Date,
    endDate: Date,
    timezone: string | null
  ): Promise<CallMonthReport[]> {
    const rows = await supabaseService.getCallsDetailedByMonth(subsidiaryId, subsidiaryName, startDate, endDate, timezone);
    return rows.map((item) => {
      const createdAt = item.createdat ? new Date(item.createdat) : new Date();
      const canceledAt = item.canceledat ? new Date(item.canceledat) : null;
      return {
        id: item.id ?? '',
        createdAt,
        clientName: item.clientname || 'Não informado',
        driverName: item.drivername || 'Não atribuído',
        destinations: Number(item.destinations) || 0,
        hasBack: Boolean(item.hasback),
        paymentType: Number(item.paymenttype) || 0,
        paymentValue: Number(item.paymentvalue) || 0,
        paymentReturnValue: Number(item.paymentreturnvalue) || 0,
        subsidiaryFeePerc: Number(item.subsidiaryfeeperc) || 0,
        status: item.status,
        statusHtml: convertStatusToBrazilian(item.status),
        salesChannelName: item.saleschannelname || null,
        subsidiaryName: item.subsidiaryname || '',
        driverCategory: Number(item.drivercategory) || 0,
        device: Number(item.device) || 0,
        canceledAt
      };
    });
  },

  async getRemittanceByPeriod(
    subsidiaryId: string,
    subsidiaryName: string | null,
    startDate: Date,
    endDate: Date,
    timezone: string | null
  ): Promise<CallRemittanceReport[]> {
    const rows = await supabaseService.getCallsDetailedByMonth(subsidiaryId, subsidiaryName, startDate, endDate, timezone);
    return rows.map((item) => mapCallRemittance(item));
  },

  async getCallsForAdjustment(params: {
    subsidiaryId: string;
    subsidiaryName: string | null;
    startDate: Date;
    endDate: Date;
    timezone: string | null;
    status: string | null;
    clientName: string | null;
  }): Promise<CallAdjustmentListItem[]> {
    const rows = await supabaseService.getCallsForAdjustment(params);
    return rows.map((item) => {
      const docValue = parseCallDoc(item.doc);
      const destinationRows = docValue?.destinations ?? [];
      const destinations = destinationRows
        .map((destination) => destination.address)
        .filter((address): address is string => !!address);
      return {
        id: item.id ?? '',
        createdAt: item.createdat ? new Date(item.createdat) : new Date(),
        clientName: item.clientname || 'Não informado',
        driverName: item.drivername || 'Não atribuído',
        status: item.status,
        statusHtml: convertStatusToBrazilian(item.status),
        destinations,
        canAdjust: docValue?.payment?.type === PaymentType.Faturado && destinationRows.length === 1
      };
    });
  },

  async updateDestinationAddress(input: {
    id: string;
    index: number;
    patch: DestinationAdjustmentPatch;
    daysAllowed: number;
  }) {
    const model = await this.getById(input.id);
    if (!model) throw new Error('Entrega não encontrada');
    if (model.payment?.type !== PaymentType.Faturado || (model.destinations ?? []).length !== 1) {
      throw new Error('Só é possível ajustar entregas faturadas com um único destino');
    }

    const createdAt = toCallDate(model.createdAt);
    if (!isCallWithinAdjustmentWindow(createdAt, input.daysAllowed)) {
      throw new Error('Prazo para ajuste desta entrega encerrou');
    }

    const { patch } = input;
    try {
      const response = await api.patch(`calls/${input.id}/destination`, {
        index: input.index,
        comments: patch.comments,
        complement: patch.complement,
        updateLocation: patch.updateLocation,
        address: patch.address,
        street: patch.street,
        number: patch.number,
        neighborhood: patch.neighborhood,
        city: patch.city,
        state: patch.state,
        placeId: patch.placeId,
        latitude: patch.latitude,
        longitude: patch.longitude,
        hasBack: patch.hasBack,
        value: patch.value,
        valueBack: patch.valueBack,
        subsidiaryFeeValue: patch.subsidiaryFeeValue,
        subsidiaryFeeValueBack: patch.subsidiaryFeeValueBack,
        distance: patch.distance,
        distanceInfo: patch.distanceInfo,
        duration: patch.duration,
        durationInfo: patch.durationInfo
      });
      return {
        synced: true,
        paymentValue: response?.data?.paymentValue ?? null,
        destinationCost: response?.data?.destinationCost ?? null,
        value: response?.data?.value ?? null,
        valueBack: response?.data?.valueBack ?? null,
        subsidiaryFeeValue: response?.data?.subsidiaryFeeValue ?? null,
        subsidiaryFeeValueBack: response?.data?.subsidiaryFeeValueBack ?? null
      };
    } catch (error: any) {
      const message = error.response?.data?.message
        ?? error.response?.data?.error?.message
        ?? 'Ops, aconteceu algo inesperado, por favor tente novamente';
      throw new Error(message);
    }
  },

  async recalculateDestination(input: {
    clientId: string;
    driverCategory: number;
    placeId: string | null;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    comments: string | null;
    complement: string;
    receiverName: string;
    receiverPhone: string;
  }) {
    try {
      const response = await api.post('destinations/by-categories', {
        address: {
          placeId: input.placeId,
          street: input.street,
          number: input.number,
          neighborhood: input.neighborhood,
          city: input.city,
          state: input.state,
          location: {
            lat: input.latitude,
            lng: input.longitude
          }
        },
        driverCategories: [input.driverCategory],
        comments: input.comments,
        complement: input.complement,
        receiverName: input.receiverName,
        receiverPhone: input.receiverPhone,
        firstDestination: true,
        clientId: input.clientId
      });
      const rows = response?.data?.data;
      const quote = Array.isArray(rows)
        ? rows.find((row: any) => Number(row.driverCategory) === Number(input.driverCategory)) ?? rows[0]
        : null;
      if (!quote) throw new Error('Não foi possível recalcular a entrega');
      return {
        value: quote.tariff?.value,
        valueBack: quote.tariff?.valueBack,
        subsidiaryFeeValue: quote.tariff?.subsidiaryFeeValue,
        subsidiaryFeeValueBack: quote.tariff?.subsidiaryFeeValueBack,
        paymentValue: quote.cost,
        distance: quote.distance,
        distanceInfo: quote.distanceInfo ?? null,
        duration: quote.duration ?? null,
        durationInfo: quote.durationInfo ?? null,
        latitude: Number(quote.latitude),
        longitude: Number(quote.longitude),
        placeId: quote.addressObj?.placeId ?? input.placeId ?? null
      };
    } catch (error: any) {
      const details = error.response?.data?.errors;
      const message = Array.isArray(details)
        ? details.map((item: any) => item.message).filter(Boolean).join(', ')
        : error.response?.data?.message
          ?? error.response?.data?.error?.message
          ?? error.message
          ?? 'Não foi possível recalcular a entrega';
      throw new Error(message);
    }
  },

  async getHeatmap(subsidiaryId: string | null, hours = 1) {
    if (!subsidiaryId) return [];
    try {
      const response = await api.get('calls/heatmap', {
        params: { subsidiaryId, hours }
      });
      const payload = response?.data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.points)) return payload.points;
      if (Array.isArray(payload?.rows)) return payload.rows;
      return [];
    } catch {
      return [];
    }
  }
};
